// src/pages/_home/manage-consumers/add-account-transaction.$id.tsx
import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'

import { Header } from '@/components/header'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { CalendarSingle } from '@/components/ui/calendar-single'

import { getConsumer } from '@/lib/api/billing'
import type { ConsumerInfoType } from '@/types'

export const Route = createFileRoute(
  '/_home/manage-consumers/add-account-transaction/$id',
)({
  loader: async ({ params: { id } }) => ({ id }),
  component: AddAccountTransactionPage,
})

function AddAccountTransactionPage() {
  const { id } = Route.useLoaderData()
  const consumerId = Number(id)

  const [formData, setFormData] = React.useState({
    entryType: '',
    type: '',
    amount: '',
    reference: '',
    date: new Date(),
    debit: '',
    credit: '',
  })
  const [calendarOpen, setCalendarOpen] = React.useState(false)

  const { data: consumerInfo } = useQuery<ConsumerInfoType>({
    queryKey: ['consumerInfo', consumerId],
    queryFn: () => getConsumer(consumerId),
    staleTime: 1000 * 60 * 5,
  })

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    console.log('Submitting Transaction', {
      ...formData,
      consumerId,
      formattedDate: formData.date.toISOString().split('T')[0],
    })
    // Submit logic here (e.g., API call)
  }

  return (
    <main className="p-6 max-w-5xl mx-auto space-y-6">
      <Header
        subroutes={[
          { route: '/manage-consumers', label: 'Manage Consumers' },
          {
            route: `/manage-consumers/view-account-ledger/${id}`,
            label: `Account Ledger (${consumerInfo?.full_name ?? `#${id}`})`,
          },
        ]}
      >
        {`Add Transaction`}
      </Header>

      <Card className="shadow-md border">
        <CardContent className="space-y-6 pt-6">
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <div className="col-span-1">
              <Label htmlFor="type">Transaction Type</Label>
              <Input
                id="type"
                name="type"
                placeholder="e.g. Penalty, Other Fee"
                value={formData.type}
                onChange={handleChange}
                required
              />
            </div>

            <div className="col-span-1">
              <Label htmlFor="amount">Amount (₱)</Label>
              <Input
                id="amount"
                name="amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="Enter amount"
                value={formData.amount}
                onChange={handleChange}
                required
              />
            </div>

            <div className="col-span-1 md:col-span-2">
              <Label htmlFor="reference">Reference</Label>
              <Input
                id="reference"
                name="reference"
                placeholder="Reference number or details"
                value={formData.reference}
                onChange={handleChange}
              />
            </div>


            <div className="col-span-1 md:col-span-2">
              <Label htmlFor="date">Transaction Date</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    {format(formData.date, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarSingle
                    selected={formData.date}
                    onSelect={(date) =>
                      date && setFormData((prev) => ({ ...prev, date }))
                    }
                    onConfirm={() => setCalendarOpen(false)}
                    onCancel={() => setCalendarOpen(false)}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="col-span-1 md:col-span-2 pt-4">
            <Label htmlFor="entryType">Debit or Credit</Label>
            <select
                id="entryType"
                name="entryType"
                value={formData.entryType}
                onChange={e =>
                setFormData(prev => ({ ...prev, entryType: e.target.value }))
                }
                required
                className="block w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm shadow-sm
                        focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            >
                <option value="" disabled>
                Select type…
                </option>
                <option value="debit">Debit</option>
                <option value="credit">Credit</option>
            </select>
            </div>

            <div className="col-span-1 md:col-span-2 pt-4">
              <Button type="submit" className="w-full">
                Submit Transaction
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </main>
  )
}

export default AddAccountTransactionPage
